import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";
import type { AuditEntry, Outage, OutagePage, Tariff } from "@spf/contracts";
import { api } from "./client.js";

/** One page of a cursor-paged list, whatever the endpoint calls its rows. */
export interface Page<T> {
  items: T[];
  nextBefore: string | null;
}

export type PagedState<T> =
  { kind: "loading" } | { kind: "error"; message: string } | ({ kind: "ready" } & Page<T>);

/** Appends a page, unless the view was reset out from under it. */
function appended<T>(previous: PagedState<T>, page: Page<T>): PagedState<T> {
  if (previous.kind !== "ready") return previous;
  return { kind: "ready", items: [...previous.items, ...page.items], nextBefore: page.nextBefore };
}

/**
 * A fresh first page laid over what is shown: its rows replace everything as
 * new as they are, and the older pages already read stay beneath them. Rows
 * are newest first and `keyOf` orders them by their cursor, as an ISO
 * instant does.
 */
function refreshed<T>(
  previous: PagedState<T>,
  page: Page<T>,
  keyOf: (item: T) => string,
): PagedState<T> {
  const cut = page.nextBefore;
  if (previous.kind !== "ready" || cut === null) return { kind: "ready", ...page };
  const older = previous.items.filter((item) => keyOf(item) < cut);
  return {
    kind: "ready",
    items: [...page.items, ...older],
    nextBefore: older.length > 0 ? previous.nextBefore : cut,
  };
}

/** When a list's head changes on its own, and how to tell its rows apart. */
export interface Refresh<T> {
  /** Each new value refreshes the first page quietly. */
  key: string | null;
  /** The value the list's cursor is compared against. */
  keyOf: (item: T) => string;
}

export interface Paged<T> {
  state: PagedState<T>;
  loadingMore: boolean;
  /** Set when the last "load older" failed; cleared when one succeeds. */
  moreFailed: boolean;
  reload: () => void;
  loadMore: () => void;
}

/**
 * Refreshes the first page quietly whenever `refresh.key` changes, as the
 * energy panels refresh: the rows stay on screen until the new ones land. The
 * first key is the page opening, which the list's own load already covers, so
 * it fetches nothing.
 */
function useQuietRefresh<T>(
  load: (before: string | undefined) => Promise<Page<T>>,
  failed: string,
  refresh: Refresh<T> | undefined,
  generation: { readonly current: number },
  setState: Dispatch<SetStateAction<PagedState<T>>>,
): void {
  const refreshKey = refresh?.key ?? null;
  const keyOf = refresh?.keyOf;
  const seen = useRef<string | null>(null);
  useEffect(() => {
    const first = seen.current === null;
    seen.current = refreshKey;
    if (first || refreshKey === null || keyOf === undefined) return;

    const mine = generation.current;
    void load(undefined).then(
      (page) => {
        if (generation.current === mine) setState((previous) => refreshed(previous, page, keyOf));
      },
      () => {
        // Rows that can no longer be kept current must not look as if they were.
        if (generation.current === mine) setState({ kind: "error", message: failed });
      },
    );
  }, [refreshKey, keyOf, load, failed, generation, setState]);
}

/**
 * A list paginated by cursor rather than by page number, matching the
 * endpoints: the audit log only ever grows at the head, so an offset would
 * shift under the reader between one page and the next.
 *
 * `load` must be stable: defined outside the component, as both below are.
 */
function usePages<T>(
  load: (before: string | undefined) => Promise<Page<T>>,
  failed: string,
  refresh?: Refresh<T>,
): Paged<T> {
  const [state, setState] = useState<PagedState<T>>({ kind: "loading" });
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreFailed, setMoreFailed] = useState(false);

  // Only the newest load may set state, and nothing resolving after unmount
  // may set it at all.
  const generation = useRef(0);

  const reload = useCallback(() => {
    const mine = (generation.current += 1);
    setState({ kind: "loading" });
    void load(undefined).then(
      (page) => {
        if (generation.current === mine) setState({ kind: "ready", ...page });
      },
      () => {
        if (generation.current === mine) setState({ kind: "error", message: failed });
      },
    );
  }, [load, failed]);

  useEffect(() => {
    reload();
    return () => {
      generation.current += 1;
    };
  }, [reload]);

  useQuietRefresh(load, failed, refresh, generation, setState);

  const loadMore = useCallback(() => {
    if (state.kind !== "ready" || state.nextBefore === null) return;

    const mine = generation.current;
    const before = state.nextBefore;
    setLoadingMore(true);
    setMoreFailed(false);
    void load(before)
      .then(
        (page) => {
          if (generation.current !== mine) return;
          setState((previous) => appended(previous, page));
        },
        () => {
          // The pages already read stay on screen. The button alone was not
          // enough of a signal: it looks identical before and after a failure,
          // so nothing told the reader the page had not arrived.
          if (generation.current === mine) setMoreFailed(true);
        },
      )
      .finally(() => {
        setLoadingMore(false);
      });
  }, [state, load]);

  return { state, loadingMore, moreFailed, reload, loadMore };
}

const AUDIT_PAGE = 25;

function loadAudit(before: string | undefined): Promise<Page<AuditEntry>> {
  const query = before === undefined ? { limit: AUDIT_PAGE } : { limit: AUDIT_PAGE, before };
  return api.audit(query).then((page) => ({ items: page.entries, nextBefore: page.nextBefore }));
}

export function useAuditLog(): Paged<AuditEntry> {
  return usePages(loadAudit, "Could not load the activity log. The server may be unreachable.");
}

const TARIFF_PAGE = 50;

function loadTariffs(before: string | undefined): Promise<Page<Tariff>> {
  const query = before === undefined ? { limit: TARIFF_PAGE } : { limit: TARIFF_PAGE, before };
  return api.tariffs(query).then((page) => ({ items: page.tariffs, nextBefore: page.nextBefore }));
}

/** The tariff table, newest rate first. */
export function useTariffs(): Paged<Tariff> {
  return usePages(loadTariffs, "Could not load the tariffs. The server may be unreachable.");
}

const OUTAGE_PAGE = 20;

/** An outage, with what the page it came on says about all of them. */
export type OutageRow = Outage & Pick<OutagePage, "timezone" | "reserveSocPct" | "gapSeconds">;

function loadOutages(before: string | undefined): Promise<Page<OutageRow>> {
  const query = before === undefined ? { limit: OUTAGE_PAGE } : { limit: OUTAGE_PAGE, before };
  return api
    .outages(query)
    .then(({ outages, nextBefore, timezone, reserveSocPct, gapSeconds }) => ({
      items: outages.map((outage) => ({ ...outage, timezone, reserveSocPct, gapSeconds })),
      nextBefore,
    }));
}

const outageStart = (outage: OutageRow) => outage.start;

/**
 * Grid outages, newest first, refreshed with each new reading so one still
 * going keeps counting.
 */
export function useOutages(refreshKey: string | null): Paged<OutageRow> {
  return usePages(
    loadOutages,
    "Could not load the outage history. The server may be unreachable.",
    {
      key: refreshKey,
      keyOf: outageStart,
    },
  );
}
